import type { Link, Parent, PhrasingContent, Root, RootContent } from "mdast";
import type { Plugin } from "unified";
import { visit } from "unist-util-visit";

// Remark plugin that converts a link to a Loom video into a custom
// `loom-embed` element, mapped to React via the `components` prop on
// ReactMarkdown. The player URL is derived from the share URL — Loom's oEmbed
// endpoint returns nothing else we need, and skipping it means no network call
// and no provider-supplied HTML to trust.
//
// A link only becomes a player when its paragraph holds nothing but Loom
// links. A URL cited mid-sentence stays a link, which is both what Slack and
// Notion do and what keeps a block-level <iframe> out of a <p>.

const LOOM_VIDEO_RE =
  /^https?:\/\/(?:www\.)?loom\.com\/(?:share|embed)\/([0-9a-f]{32})(?:[/?#].*)?$/i;
const LOOM_EMBED_HNAME = "loom-embed";

interface LoomEmbedProperties {
  videoId: string;
  title?: string;
}

/**
 * The 32-hex video id in a Loom share or embed URL, or null for any other URL.
 * The id guard is what rejects `loom.com/share/folder/...` and every other
 * non-video path on the host.
 */
export function parseLoomVideoId(href: string): string | null {
  return LOOM_VIDEO_RE.exec(href)?.[1]?.toLowerCase() ?? null;
}

function isInsignificant(node: PhrasingContent): boolean {
  if (node.type === "break") return true;
  return node.type === "text" && node.value.trim() === "";
}

function isLoomLink(node: PhrasingContent): node is Link {
  return node.type === "link" && parseLoomVideoId(node.url) !== null;
}

function linkText(link: Link): string {
  let text = "";
  visit(link, "text", (node) => {
    text += node.value;
  });
  return text.trim();
}

function loomEmbedBlock(link: Link): RootContent {
  const videoId = parseLoomVideoId(link.url);
  if (videoId === null) {
    throw new Error(`loomEmbedBlock called with a non-Loom url: ${link.url}`);
  }

  // An autolinked URL renders its own href as its text; only a `[label](url)`
  // link carries a title worth passing through as the player's name.
  const text = linkText(link);
  const hasLabel = text !== "" && text !== link.url && !link.url.endsWith(text);
  const hProperties: LoomEmbedProperties = hasLabel ? { videoId, title: text } : { videoId };

  // mdast-util-to-hast honors `data.hName` regardless of node type, so this
  // renders as <loom-embed> rather than <p>. `paragraph` is the node type
  // because it is valid block content in root, blockquote, and listItem alike.
  return {
    type: "paragraph",
    children: [],
    data: { hName: LOOM_EMBED_HNAME, hProperties },
  } as unknown as RootContent;
}

export const remarkLoomEmbeds: Plugin<[], Root> = () => {
  return (tree) => {
    visit(tree, "paragraph", (paragraph, index, parent: Parent | undefined) => {
      if (index == null || !parent) return;

      const significant = paragraph.children.filter((child) => !isInsignificant(child));
      if (significant.length === 0 || !significant.every(isLoomLink)) {
        return;
      }

      const blocks = significant.map(loomEmbedBlock);
      (parent.children as RootContent[]).splice(index, 1, ...blocks);
      return index + blocks.length;
    });
  };
};
